import { ConfigProvider, Flex, Form, TimePicker } from 'antd';
import { Dayjs } from 'dayjs';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { PlotterMode } from 'business/data-analysis/constants';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { GraphFilters } from 'business/data-analysis/pages/graph/types';
import { Module_Enum, useGetCurrentRingQuery } from 'generated/graphql';
import { TIME_HOUR_MINUTE_FORMAT } from 'technical/string/formatter';
import { DateRangeValue } from 'technical/types';
import { notEmpty } from 'technical/utils/not-empty';
import { FilterCard } from 'ui/filter-card';
import { DateRangePicker } from 'ui/form/date-range-picker';
import NumberRangeInput from 'ui/form/number-range-input';

import { OnChangeGraphFilters } from './hooks/types';
import styles from './index.module.scss';

interface Props {
  filters: GraphFilters;
  onChangeFilters: OnChangeGraphFilters;
}

const DataAnalysisFilterCard = ({ children }: PropsWithChildren) => {
  return <FilterCard>{children}</FilterCard>;
};

const withDate = (timePart: Dayjs, datePart: Dayjs) =>
  timePart.year(datePart.year()).month(datePart.month()).date(datePart.date());

const withTime = (datePart: Dayjs, timePart: Dayjs) =>
  datePart
    .hour(timePart.hour())
    .minute(timePart.minute())
    .second(timePart.second());

const TimePeriodFilterCard = ({
  filters,
  onChangeFilters,
}: Omit<Props, 'mode'>) => {
  const { t } = useTranslation();
  const { startDate, endDate } = filters.dateFilters;

  const handleDateRangeChange = (dateRange: DateRangeValue) => {
    onChangeFilters.onChangeDateRange([
      dateRange?.[0] ? withDate(startDate, dateRange[0]) : null,
      dateRange?.[1] ? withDate(endDate, dateRange[1]) : null,
    ]);
  };

  const handleStartTimeChange = (time: Dayjs | null) => {
    if (!time) {
      return;
    }
    onChangeFilters.onChangeDateRange([withTime(startDate, time), null]);
  };

  const handleEndTimeChange = (time: Dayjs | null) => {
    if (!time) {
      return;
    }
    onChangeFilters.onChangeDateRange([null, withTime(endDate, time)]);
  };

  return (
    <DataAnalysisFilterCard>
      <Flex gap={8}>
        <Form.Item
          layout={'vertical'}
          label={t('dataAnalysis.labels.dateFilter')}
        >
          <DateRangePicker
            allowClear
            value={[startDate, endDate]}
            onChange={handleDateRangeChange}
            className={styles.dateRange}
          />
        </Form.Item>
        <Form.Item
          layout={'vertical'}
          label={t('dataAnalysis.labels.startTime')}
        >
          <TimePicker
            value={startDate}
            onChange={handleStartTimeChange}
            format={TIME_HOUR_MINUTE_FORMAT}
            className={styles.timePicker}
          />
        </Form.Item>
        <Form.Item layout={'vertical'} label={t('dataAnalysis.labels.endTime')}>
          <TimePicker
            value={endDate}
            onChange={handleEndTimeChange}
            format={TIME_HOUR_MINUTE_FORMAT}
            className={styles.timePicker}
          />
        </Form.Item>
      </Flex>
    </DataAnalysisFilterCard>
  );
};

const RingFilterCard = ({ filters, onChangeFilters }: Omit<Props, 'mode'>) => {
  const { t } = useTranslation();

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.DataAnalysis);

  const { constructionSiteId } = usePermissions();
  const { data, error } = useGetCurrentRingQuery({
    variables: { constructionSiteId },
    context: { headers: graphqlHeaders },
  });

  const [minRing, maxRing] = filters.ringFilters ?? [];

  const ringModeErrorMessage =
    notEmpty(minRing) && notEmpty(maxRing) && minRing > maxRing
      ? t('errors.invalid_ring_range')
      : undefined;

  return (
    <DataAnalysisFilterCard>
      <Flex vertical className={styles.numberRange}>
        <Form.Item layout={'vertical'} label={t('input.ringRange.label')}>
          {/* Cannot use the NumberRangeInputWithMessage here due to the use of
          `value` and `onChange` props */}
          <NumberRangeInput
            allowClear
            value={filters.ringFilters}
            onChange={onChangeFilters.onChangeRingRange}
            leftInputProps={{
              placeholder: t('input.ringRange.startPlaceholder'),
            }}
            rightInputProps={{
              placeholder: t('input.ringRange.endPlaceholder'),
            }}
            errorMessage={ringModeErrorMessage}
            bottomMessage={
              error
                ? t('dataAnalysis.errors.currentRingNumberError')
                : t('dataAnalysis.labels.currentRingNumber', {
                    ringNumber:
                      data?.parameter_ring_aggregate.aggregate?.max?.ringNumber,
                  })
            }
          />
        </Form.Item>
      </Flex>
    </DataAnalysisFilterCard>
  );
};

export const FilterCardByMode = ({ filters, onChangeFilters }: Props) => {
  const mode = useMode();
  return (
    <div className={styles.filterCard}>
      <ConfigProvider
        theme={{
          components: {
            Form: {
              itemMarginBottom: 0,
            },
          },
        }}
      >
        {mode === PlotterMode.TIME_PERIOD ? (
          <TimePeriodFilterCard
            filters={filters}
            onChangeFilters={onChangeFilters}
          />
        ) : (
          <RingFilterCard filters={filters} onChangeFilters={onChangeFilters} />
        )}
      </ConfigProvider>
    </div>
  );
};
